import { addDays, addMonths, endOfMonth, startOfMonth } from "date-fns";
import ApiController, { ApiRequest } from "../controller";

class BookingModule extends ApiController {
  resource = "/booking";

  services() {
    const request = new ApiRequest({
      method: "GET",
      url: `/services`,
    });

    return this.request<[any]>(request);
  }

  async timetableMonth(month, voucher, pass) {
    const monthStamp = month.getTime() / 1000 - month.getTimezoneOffset() * 60;

    const data = {
      month: monthStamp,
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    }

    const request = new ApiRequest({
      method: "POST",
      url: `/timetableMonth`,
      body: data,
    });

    const response = await this.request<any>(request);

    if (response?.dates) {
      for (let i = 0; i < response.dates.length; i++) {
        const dateInfo = response.dates[i];
        dateInfo.date = new Date(dateInfo.date * 1000);
      }
    }

    return response;
  }

  async timetableWeek(date, voucher, pass) {
    const dateStamp = date.getTime() / 1000 - date.getTimezoneOffset() * 60;

    const data = {
      date: dateStamp,
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    }

    const request = new ApiRequest({
      method: "POST",
      url: `/timetableWeek`,
      body: data,
    });

    const response = await this.request<any>(request);

    const services = {};
    const employees = {};

    for (let i = 0; i < response?.services.length; i++) {
      const service = response?.services[i];
      services[service.id] = service;
    }

    for (let i = 0; i < response?.employees.length; i++) {
      const employee = response?.employees[i];
      employees[employee.id] = employee;
    }

    for (let i = 0; i < response?.dates.length; i++) {
      const date = response?.dates[i];

      for (let j = 0; j < date.slots.length; j++) {
        const slot = date.slots[j];

        for (let k = 0; k < slot.services.length; k++) {
          const slotService = slot.services[k];

          slotService.service = services[slotService.service.id];
          slotService.employee = employees[slotService.employee.id];
        }
      }
    }

    return response;
  }

  async timetableDay(date, voucher, pass) {
    const dateStamp = date.getTime() / 1000 - date.getTimezoneOffset() * 60;

    const data = {
      date: dateStamp,
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    }

    const request = new ApiRequest({
      method: "POST",
      url: `/timetableDay`,
      body: data,
    });

    const response = await this.request<any>(request);

    const services = {};
    const employees = {};

    for (let i = 0; i < response?.services.length; i++) {
      const service = response?.services[i];
      services[service.id] = service;
    }

    for (let i = 0; i < response?.employees.length; i++) {
      const employee = response?.employees[i];
      employees[employee.id] = employee;
    }

    for (let j = 0; j < response?.slots.length; j++) {
      const slot = response?.slots[j];

      for (let k = 0; k < slot.services.length; k++) {
        const slotService = slot.services[k];

        slotService.service = services[slotService.service.id];
        slotService.employee = employees[slotService.employee.id];
      }
    }

    return response;
  }

  lastMinute(voucher, pass) {
    const data = {
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    }

    const request = new ApiRequest({
      method: "POST",
      url: `/lastMinute`,
      body: data,
    });

    return this.request<any>(request);
  }

  async lastMinuteAvailability(date, from, to, service, voucher, pass) {
    const data = {
      date,
      from,
      to,
      service: service.id,
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    }

    const request = new ApiRequest({
      method: "POST",
      url: `/lastMinuteAvailability`,
      body: data,
    });

    const response = await this.request<any>(request);

    const services = {};
    const employees = {};

    for (let i = 0; i < response?.services.length; i++) {
      const service = response?.services[i];
      services[service.id] = service;
    }

    for (let i = 0; i < response?.employees.length; i++) {
      const employee = response?.employees[i];
      employees[employee.id] = employee;
    }

    for (let i = 0; i < response?.slots.length; i++) {
      const slot = response?.slots[i];

      for (let j = 0; j < slot.services.length; j++) {
        const slotService = slot.services[j];

        slotService.service = services[slotService.service.id];
        slotService.employee = employees[slotService.employee.id];
      }
    }

    return response;
  }

  async monthAvailability(month, serviceSelections, moveReservation, voucher, pass) {
    const services: any[] = [];

    for (let i = 0; i < serviceSelections.length; i++) {
      const serviceSelection = serviceSelections[i];

      const serviceInfo: any = {
        service: serviceSelection.service.id,
      };

      if (serviceSelection.selectedEmployee) {
        serviceInfo.employee = serviceSelection.selectedEmployee.id;
      }

      services.push(serviceInfo);
    }

    const start: Date = startOfMonth(new Date(month));
    const end: Date = addDays(endOfMonth(addMonths(new Date(month), 1)), 1)

    const startDate = parseInt(start.getTime() / 1000 - start.getTimezoneOffset() * 60);
    const endDate = parseInt(end.getTime() / 1000 - end.getTimezoneOffset() * 60);

    const request: any = {
      startDate,
      endDate,
      services,
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    };

    if (moveReservation) {
      if (moveReservation.idReservationCustomer) {
        request.moveReservationCustomer = moveReservation.idReservationCustomer;
      } else {
        request.moveReservation = moveReservation.id;
      }
    }

    const response = await this.request<any>(new ApiRequest({
      method: "POST",
      url: `/monthAvailability`,
      body: request,
    }));

    if (response?.dates) {
      for (let i = 0; i < response.dates.length; i++) {
        const dateInfo = response.dates[i];
        dateInfo.date = new Date(dateInfo.date * 1000);
      }
    }

    return response;
  }

  async dayAvailability(date, serviceSelections, moveReservation, voucher, pass) {
    const services: any[] = [];

    for (let i = 0; i < serviceSelections.length; i++) {
      const serviceSelection = serviceSelections[i];

      const serviceInfo: any = {
        service: serviceSelection.service.id,
      };

      if (serviceSelection.selectedEmployee) {
        serviceInfo.employee = serviceSelection.selectedEmployee.id;
      }

      services.push(serviceInfo);
    }

    const dateStamp = date.getTime() / 1000 - date.getTimezoneOffset() * 60;

    const request: any = {
      date: dateStamp,
      services,
      voucher: voucher ? voucher.code : null,
      pass: pass ? pass.id : null,
    };

    if (moveReservation) {
      if (moveReservation.idReservationCustomer) {
        request.moveReservationCustomer = moveReservation.idReservationCustomer;
      } else {
        request.moveReservation = moveReservation.id;
      }
    }

    const response = await this.request<any>(new ApiRequest({
      method: "POST",
      url: `/dayAvailability`,
      body: request,
    }));

    const _services: any = {};
    const _employees: any = {};

    if (response?.services) {
      for (let i = 0; i < response.services.length; i++) {
        const service = response.services[i];
        _services[service.id] = service;
      }
    }

    if (response?.employees) {
      for (let i = 0; i < response.employees.length; i++) {
        const employee = response.employees[i];
        _employees[employee.id] = employee;
      }
    }

    if (response?.slots) {
      for (let j = 0; j < response.slots.length; j++) {
        const slot = response.slots[j];

        for (let k = 0; k < slot.services.length; k++) {
          const slotService = slot.services[k];

          slotService.service = _services[slotService.service.id];
          slotService.employee = _employees[slotService.employee.id];
        }
      }
    }

    return response;
  }

  async waitlistAvailability(month, serviceSelections) {
    const services: any[] = [];

    for (let i = 0; i < serviceSelections.length; i++) {
      const serviceSelection = serviceSelections[i];

      const serviceInfo: any = {
        service: serviceSelection.service.id,
      };

      if (serviceSelection.selectedEmployee) {
        serviceInfo.employee = serviceSelection.selectedEmployee.id;
      }

      services.push(serviceInfo);
    }

    // const start: Date = new Date(new Date(month).setDate(1));
    // const end: Date = new Date(new Date(month).setDate(1));

    const start = startOfMonth(new Date(month));
    const end = addDays(endOfMonth(new Date(month)), 1);

    const startDate = Math.round(start.getTime() / 1000 - start.getTimezoneOffset() * 60);
    const endDate = Math.round(end.getTime() / 1000 - end.getTimezoneOffset() * 60);

    const request: any = {
      startDate,
      endDate,
      services,
    };

    const response = await this.request<any>(new ApiRequest({
      method: "POST",
      url: `/waitlistAvailability`,
      body: request,
    }));

    if (response?.dates) {
      for (let i = 0; i < response.dates.length; i++) {
        const dateInfo = response.dates[i];
        dateInfo.date = new Date(dateInfo.date * 1000);
      }
    }

    return response;
  }

  async waitlist(customer, serviceSelections, dates, moveReservation) {
    const requestCustomer: any = {};

    if (customer.id) {
      requestCustomer.id = customer.id;
    } else {
      requestCustomer.attributes = customer.fields;
      requestCustomer.customFields = customer.customFields;
      requestCustomer.acceptConsent = customer.privacyConsentAccepted;
      requestCustomer.acceptMarketingConsent = customer.marketingConsentAccepted;
    }

    const services: any[] = [];

    for (let i = 0; i < serviceSelections.length; i++) {
      const serviceSelection = serviceSelections[i];

      const serviceInfo: any = {
        service: serviceSelection.service.id,
      };

      if (serviceSelection.selectedEmployee) {
        serviceInfo.employee = serviceSelection.selectedEmployee.id;
      }

      services.push(serviceInfo);
    }

    const request: any = {
      customer: requestCustomer,
      services,
      dates,
    };

    if (moveReservation) {
      if (moveReservation.idReservationCustomer) {
        request.moveReservationCustomer = moveReservation.idReservationCustomer;
      } else {
        request.moveReservation = moveReservation.id;
      }
    }

    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/waitlist`,
      body: request,
    }));
  }

  customer(email?) {
    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/customer`,
      body: { email },
    }));
  }

  async slot(data) {
    const response = await this.request<any>(new ApiRequest({
      method: "POST",
      url: `/slot`,
      body: { data },
    }));

    const services = {};
    const employees = {};

    if (response?.services) {
      for (let i = 0; i < response.services.length; i++) {
        const service = response.services[i];
        services[service.id] = service;
      }
    }

    if (response?.employees) {
      for (let i = 0; i < response.employees.length; i++) {
        const employee = response.employees[i];
        employees[employee.id] = employee;
      }
    }

    if (response?.slot) {
      for (let i = 0; i < response.slot.services.length; i++) {
        const slotService = response.slot.services[i];

        slotService.service = services[slotService.service.id];
        slotService.employee = employees[slotService.employee.id];
      }
    }

    return response;
  }

  newCustomer(fields, customFields, acceptedPrivacyConsent, acceptedMarketingConsent) {
    const request = {
      customer: fields,
      customFields,
      acceptConsent: acceptedPrivacyConsent,
      acceptMarketingConsent: acceptedMarketingConsent,
    };

    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/newCustomer`,
      body: request,
    }));
  }

  validateCustomer(fields, customFields, privacyConsentAccepted, marketingConsentAccepted) {
    const request = {
      customer: fields,
      customFields,
      acceptConsent: privacyConsentAccepted,
      acceptMarketingConsent: marketingConsentAccepted,
      validateOnly: true,
    };

    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/newCustomer`,
      body: request,
    }));
  }

  confirm(action, customer, slot, { fields, customFields, termsAccepted, giftCardVoucherCode, voucher, giftCards, paymentData, paymentParams, moveReservation, rebookReservation, waitlist, rwg_token }) {
    const requestCustomer: any = {};

    if (customer.id) {
      requestCustomer.id = customer.id;
    } else {
      requestCustomer.attributes = customer.fields;
      requestCustomer.customFields = customer.customFields;
      requestCustomer.acceptConsent = customer.privacyConsentAccepted;
      requestCustomer.acceptMarketingConsent = customer.marketingConsentAccepted;
    }

    const request: any = {
      action,
      customer: requestCustomer,
      slot: { data: slot.data },
      fields,
      customFields,
      termsAccepted,
      payment: {},
    };

    if (giftCardVoucherCode) {
      request.giftCardVoucherRequest = giftCardVoucherCode;
    }

    if (voucher) {
      request.voucher = { code: voucher.code };
    }

    if (giftCards) {
      request.payment.giftCards = giftCards.map((giftCard) => {
        return giftCard.number;
      });
    }

    if (paymentData) {
      request.payment.data = paymentData;
    }

    if (paymentParams) {
      request.payment.params = paymentParams;
    }

    if (moveReservation) {
      if (moveReservation.idReservationCustomer) {
        request.moveReservationCustomer = moveReservation.idReservationCustomer;
      } else {
        request.moveReservation = moveReservation.id;
      }
    }

    if (rebookReservation) {
      if (rebookReservation.idReservationCustomer) {
        request.rebookReservationCustomer = rebookReservation.idReservationCustomer;
      } else {
        request.rebookReservation = rebookReservation.id;
      }
    }

    if (waitlist) {
      request.waitlist = waitlist;
    }

    if (rwg_token) {
      request.rwg_token = rwg_token;
    }
    
    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/confirm`,
      body: request,
    }));
  }

  reservation(id, date, customer, signature) {
    const request = {
      id,
      date,
      customer: customer.id,
      signature,
    };

    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/reservation`,
      body: request,
    }));
  }

  voucher(code) {
    const request = {
      code,
    };

    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/voucher`,
      body: request,
    }));
  }

  customerPass(id, signature) {
    const request = {
      id,
      signature,
    };

    return this.request<any>(new ApiRequest({
      method: "POST",
      url: `/customerPass`,
      body: request,
    }));
  }
}

export default BookingModule;
